import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ModalBackdrop,
  ModalContent,
  CloseButton,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ButtonContainer,
  PrimaryButton,
  SecondaryButton
} from './styles';
import Spinner from '../Spinner';

interface BookingAuthModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const BookingAuthModal: React.FC<BookingAuthModalProps> = ({
  isVisible,
  onClose
}) => {
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVisible, isLoading, onClose]);

  // Reset loading state when modal is closed
  useEffect(() => {
    if (!isVisible) {
      setIsLoading(false);
    }
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleSignIn = () => {
    setIsLoading(true);
    // Navigate to login page using React Router, preserving return path
    history.push('/login', { from: location.pathname });
  };

  const handleSignUp = () => {
    setIsLoading(true);
    // Navigate to signup page using React Router, preserving return path
    history.push('/signup', { from: location.pathname });
  };

  return (
    <ModalBackdrop onClick={handleBackdropClick}>
      <ModalContent>
        <CloseButton
          onClick={onClose}
          aria-label="Cerrar modal"
          disabled={isLoading}
          style={{ opacity: isLoading ? 0.5 : 1 }}
        >
          ×
        </CloseButton>

        <ModalHeader>
          <ModalTitle>Inicia sesión para completar tu reserva</ModalTitle>
          <ModalSubtitle>
            Para garantizar la seguridad de tu reserva, necesitas tener una cuenta con nosotros.
          </ModalSubtitle>
        </ModalHeader>

        <ButtonContainer>
          <PrimaryButton onClick={handleSignIn} disabled={isLoading}>
            {isLoading ? <Spinner size={24} color="#ffffff" thickness={2} /> : 'Iniciar Sesión'}
          </PrimaryButton>
          <SecondaryButton onClick={handleSignUp} disabled={isLoading}>
            Crear Cuenta
          </SecondaryButton>
        </ButtonContainer>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default BookingAuthModal;